// ** React Imports
import React, { lazy, Suspense } from 'react';

// ** Utils
import { AuthProvider, useAuth } from '../store/AuthProvider';

// ** Custom Components
import LayoutWrapper from '../../components/layouts/components/layout-wrapper';

// ** Router Components
import {
  BrowserRouter as AppRouter,
  Redirect,
  Route,
  Switch,
} from 'react-router-dom';

// ** Routes & Default Routes
import { DefaultRouteEntry, RouteEntries, RouteEntry } from './routeEntries';

// ** Layouts
import { CompaniesProvider } from '@src/@data/store/CompaniesProvider';
import { CompanyProvider } from '@src/@data/store/CompanyProvider';
import { CachedCompaniesProvider } from '@store/CachedCompanyProvider/CachedCompaniesProvider';
import { NotificationsProvider } from '@src/@data/store/NotificationsProvider';
import { AppSpinner } from '@src/components/atoms/AppSpinner';
import BlankLayout from '@src/components/layouts/BlankLayout';
import VerticalLayout, {
  VerticalLayoutProps,
} from '@src/components/layouts/VerticalLayout';
import { FeatureFlagsProvider } from '@data/store/FeatureFlagsProvider';
import { PrintingProvider } from '@src/@data/store/PrintingProvider';
import themeConfig from '../configs/themeConfig';
import { ROUTE_AUTH_SIGN_IN, ROUTE_NOT_FOUND } from './consts';
import { FinalRoute } from './FinalRoute';

// ** All of the available layouts
const Layouts: Record<string, React.ComponentType<VerticalLayoutProps>> = {
  BlankLayout,
  VerticalLayout,
};

// ** Default Layout
const DEFAULT_LAYOUT: keyof typeof Layouts = 'VerticalLayout';

// ** Return Filtered Array of Routes & Paths
const getLayoutRoutesAndPaths = (layout: string) => {
  const LayoutRoutes: RouteEntry[] = [];
  const LayoutPaths: string[] = [];

  Object.values(RouteEntries).forEach((route) => {
    // ** Checks if Route layout or Default layout matches current layout
    if (
      route.layout === layout ||
      (route.layout === undefined && DEFAULT_LAYOUT === layout)
    ) {
      LayoutRoutes.push(route);
      LayoutPaths.push(route.path);
    }
  });

  return { LayoutRoutes, LayoutPaths };
};

/**
 * segregates all the routes based on the layouts and resolves all those routes inside layouts
 */
const resolveLayoutRoutes = () => {
  return Object.keys(Layouts).map((layoutKey, index) => {
    // Note: make sure to keep layout and component name equal
    const LayoutTag = Layouts[layoutKey as keyof typeof Layouts];
    const { LayoutRoutes, LayoutPaths } = getLayoutRoutesAndPaths(layoutKey);

    return (
      <Route path={LayoutPaths} key={index}>
        <Switch>
          {LayoutRoutes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              exact={route.exact === true}
              render={(routeProps) => (
                <LayoutTag {...route.layoutProps}>
                  <LayoutWrapper
                    transition={themeConfig.layout.routerTransition}
                    appLayout={route.appLayout}
                    routeMeta={route.meta}
                    wrapperClass={route.className}
                  >
                    <Suspense fallback={<AppSpinner />}>
                      <FinalRoute route={route} {...routeProps} />
                    </Suspense>
                  </LayoutWrapper>
                </LayoutTag>
              )}
            />
          ))}
        </Switch>
      </Route>
    );
  });
};

const Router = () => {
  const { user } = useAuth();
  const NotFound = lazy(() => import('@src/pages/Error'));

  return (
    <AppRouter basename={process.env.REACT_APP_BASENAME}>
      <AuthProvider>
        <CompaniesProvider>
          <CachedCompaniesProvider>
            <CompanyProvider>
              <FeatureFlagsProvider>
                <NotificationsProvider>
                  <PrintingProvider>
                    <Switch>
                      <Route
                        exact
                        path="/"
                        render={() =>
                          user ? (
                            <Redirect to={DefaultRouteEntry.path} />
                          ) : (
                            <Redirect to={ROUTE_AUTH_SIGN_IN} />
                          )
                        }
                      />
                      {resolveLayoutRoutes()}
                      <Route
                        path={['*', ROUTE_NOT_FOUND]}
                        component={NotFound}
                      />
                    </Switch>
                  </PrintingProvider>
                </NotificationsProvider>
              </FeatureFlagsProvider>
            </CompanyProvider>
          </CachedCompaniesProvider>
        </CompaniesProvider>
      </AuthProvider>
    </AppRouter>
  );
};

export default Router;
